import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, MapPin } from 'lucide-react';
import { ServicePageLayout } from '../components/ServicePageLayout';
import { NotFoundPage } from './NotFoundPage';
import { getCity } from '../data/cityData';
import { packages, extras } from '../data/packages';
import { BUSINESS_ID, cityPath } from '../utils/seo';
/** City-specific wording for each package on featured city pages. */
const PACKAGE_COPY: Record<string, (city: string) => string> = {
  'basic-wash': city => `A maintenance wash in ${city} covering the exterior, a light wipe-down, vacuum, rims, tires, and windows.`,
  'wash-wax': city => `Wash and wax in ${city} — an exterior wash, vacuum, light interior wipe-down, tire dressing, and hybrid ceramic spray for gloss and water beading.`,
  'full-detail': city => `Our most popular full detail in ${city}: the complete interior detail plus exterior wash, clay bar treatment, rims, tires, and hybrid ceramic spray.`,
  'interior-detail': city => `Mobile interior detailing in ${city} — shampooed seats and floor mats, a deep interior clean, vacuum, and steam sanitizing.`
};
const relatedServices = [{
  label: 'Interior Detailing',
  to: '/services/interior-detailing'
}, {
  label: 'Exterior Detailing',
  to: '/services/exterior-detailing'
}, {
  label: 'Full Car Detail',
  to: '/full-detail'
}, {
  label: 'Engine Bay Detailing',
  to: '/services/engine-bay-detailing'
}, {
  label: 'Headlight Restoration',
  to: '/services/headlight-restoration'
}, {
  label: 'Mobile Detailing',
  to: '/services/mobile-detailing'
}, {
  label: 'Hybrid Ceramic Spray',
  to: '/services/hybrid-ceramic-spray'
}];
export function CityPage({
  slug: slugProp
}: {
  slug?: string;
} = {}) {
  const params = useParams<{
    slug: string;
  }>();
  const slug = slugProp ?? params.slug;
  const city = slug ? getCity(slug) : undefined;
  // An unknown city slug used to silently redirect to the homepage, which
  // search engines read as a soft 404 (a real page answering an invalid URL).
  // Serve the noindexed 404 instead.
  if (!city) {
    return <NotFoundPage data-id="element-630" data-mp="1nxzgreys1ybe" />;
  }
  const baseUrl = 'https://www.fulldetailpro.com';
  const path = cityPath(city.slug);
  // Described as a Service delivered to this city rather than a LocalBusiness
  // located in it — Mike's Pro Detail is mobile and has no storefront anywhere.
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Mobile Car Detailing in ${city.name}, CA`,
    serviceType: 'Mobile Car Detailing',
    description: city.metaDescription,
    url: `${baseUrl}${path}`,
    provider: {
      '@id': BUSINESS_ID
    },
    areaServed: {
      '@type': 'City',
      name: `${city.name}, CA`
    }
  };
  const breadcrumbs = [{
    name: 'Home',
    item: `${baseUrl}/`
  }, {
    name: 'Locations',
    item: `${baseUrl}/locations`
  }, {
    name: city.name,
    item: `${baseUrl}${path}`
  }];
  return <ServicePageLayout title={city.title} subtitle={city.subtitle} metaDescription={city.metaDescription} schema={schema} faqs={city.faqs} breadcrumbs={breadcrumbs} cityName={city.name} seoTitle={city.seoTitle} data-id="element-631" data-mp="2qdr9lp6vh69r">
      {city.paragraphs.map((p, i) => <p key={i} data-id="element-632" data-mp="2nxjciysayn1s" data-mpi={i}>{p}</p>)}

      {city.featured && <>
          <h2 data-id="element-633" data-mp="2nz4br1pkau5z">We Come To You</h2>
          <p data-id="element-634" data-mp="2svus7q5a3rrx">
            Skip the trip to the detailing shop. Mike's Pro Detail brings
            professional mobile detailing directly to your home, office, or
            another convenient location in {city.name}. We don't operate a
            storefront — we travel to every appointment with our own water and
            detailing equipment. We require access to a standard electrical
            outlet at the service location.
          </p>

          <h2 data-id="element-635" data-mp="3bt5od1vm6535">Detailing Packages Available in {city.name}</h2>
          <p data-id="element-636" data-mp="3ewyw2oe1uf7">
            Every package is performed on-site, at the same prices listed on our{' '}
            <Link to="/services" data-id="element-637" data-mp="865e5fcega0a">detailing services page</Link>. Prices shown are
            SM/Mid vehicles · SUV/Cross vehicles.
          </p>
          <ul className="space-y-4 my-6 list-none pl-0" data-id="element-638" data-mp="2gkv8eyujulhs">
            {packages.map(pkg => <li key={pkg.slug} className="flex items-start" data-id="element-639" data-mp="2bma9d2r1a7ux">
                <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-640" data-mp="26o8t0pkzsmn5" />
                <span data-id="element-641" data-mp="21pp4qiixeyrm">
                  <strong data-id="element-642" data-mp="21qgyrowvkofs">
                    <Link to={pkg.path} data-id="element-643" data-mp="21r8jg4oxp1ra">{pkg.name}</Link> — {pkg.priceSm} ·{' '}
                    {pkg.priceSuv}
                    {pkg.requirement ? ` (${pkg.requirement})` : ''}:
                  </strong>{' '}
                  {PACKAGE_COPY[pkg.slug]?.(city.name) ?? pkg.summary}
                </span>
              </li>)}
          </ul>
          <p data-id="element-644" data-mp="2kojippakfv6q">
            Steam & sanitize is part of every Interior Detail and Full Detail.
            These extra services can be added to any visit in {city.name}:
          </p>
          <ul className="space-y-3 my-6 list-none pl-0" data-id="element-645" data-mp="3i60ekpdgsn4a">
            {extras.map(extra => <li key={extra.name} className="flex items-start" data-id="element-646" data-mp="3dbntkawcb3qf">
                <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-647" data-mp="3lmmrg4ecucpt" />
                <span data-id="element-648" data-mp="3pn6n13ja589u">
                  <strong data-id="element-649" data-mp="3pmojrmy79tsf">{extra.name}</strong> — {extra.price}
                </span>
              </li>)}
          </ul>
          <p data-id="element-650" data-mp="3ulrot7e6k00e">
            Learn more about{' '}
            <Link to="/services/engine-bay-detailing" data-id="element-651" data-mp="3g8d44c6c0qw">engine bay detailing</Link>{' '}
            and{' '}
            <Link to="/services/headlight-restoration" data-id="element-652" data-mp="8fkzknz4v5sy">
              headlight restoration
            </Link>
            , or see every option on our{' '}
            <Link to="/services" data-id="element-653" data-mp="cfd8j1sfvdyu">services page</Link>.
          </p>

          <h2 data-id="element-654" data-mp="h7d98wgc2awh">Serving {city.name} and Nearby Communities</h2>
          <p data-id="element-655" data-mp="m6q4a4hcyoa5">
            Beyond {city.name} itself, our mobile detailing service covers the
            rest of the Conejo Valley —{' '}
            <Link to={cityPath('newbury-park')} data-id="element-656" data-mp="r6vo4yhdz6ho">Newbury Park</Link>,{' '}
            <Link to={cityPath('westlake-village')} data-id="element-657" data-mp="w4npsk7u26z2">Westlake Village</Link>,{' '}
            <Link to={cityPath('oak-park')} data-id="element-658" data-mp="w2jaz78ebnk0">Oak Park</Link>, and{' '}
            <Link to={cityPath('agoura-hills')} data-id="element-659" data-mp="6h9trz60oxv7">Agoura Hills</Link> — as well as{' '}
            <Link to={cityPath('moorpark')} data-id="element-660" data-mp="1izzdqs4a1an">Moorpark</Link> and{' '}
            <Link to={cityPath('simi-valley')} data-id="element-661" data-mp="g736x4lt5l1k">Simi Valley</Link> to the north.
            Wherever you are, we drive to you — no drop-off and no waiting room.
            Have a question first? Check our <Link to="/faq" data-id="element-662" data-mp="p6zrau15zxqt">FAQ</Link> or{' '}
            <Link to="/contact" data-id="element-663" data-mp="p5ol6nk8ydwj">contact us to book</Link>.
          </p>
        </>}

      {/* Featured cities show exact packages and add-ons above instead. */}
      {!city.featured && <>
      <h2 data-id="element-664" data-mp="z4e2ae4bjubq">Mobile Detailing Services in {city.name}</h2>
      <p data-id="element-665" data-mp="u6cmc226wb4h">
        We bring the full range of professional auto detailing to {city.name},
        with every service performed on-site at your home or office. Popular
        options for {city.name} drivers include:
      </p>
      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-666" data-mp="2vyhar3khcqag">
        <li className="flex items-start" data-id="element-667" data-mp="2vzsgva1eea4q">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-668" data-mp="1bfew005e12ih" />
          <span data-id="element-669" data-mp="1gertdetvxrzb">
            <strong data-id="element-670" data-mp="1gf9mi2l93q30">Interior Detailing:</strong> Deep vacuuming, shampooing,
            steam sanitizing, and leather conditioning for a like-new cabin.
          </span>
        </li>
        <li className="flex items-start" data-id="element-671" data-mp="16oyjci9e5kx6">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-672" data-mp="16nwwlgj0j9zm" />
          <span data-id="element-673" data-mp="spfdxx7ysnok">
            <strong data-id="element-674" data-mp="soxfctxtxxdh">Exterior Detailing:</strong> Safe hand wash, clay bar
            decontamination, and durable wax or sealant protection.
          </span>
        </li>
        <li className="flex items-start" data-id="element-675" data-mp="iqzm1s145ae0">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-676" data-mp="isay0f4ppog7" />
          <span data-id="element-677" data-mp="npklvqcgds1x">
            <strong data-id="element-678" data-mp="nr5fndqy16yq">Full Car Detail:</strong> Our most popular bumper-to-bumper
            package combining interior and exterior services.
          </span>
        </li>
        <li className="flex items-start" data-id="element-679" data-mp="2i3vapyt1sm7p">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-680" data-mp="2i2smjyn40xvh" />
          <span data-id="element-681" data-mp="14xscme661zro">
            <strong data-id="element-682" data-mp="14w7i2ojuqgtt">Engine Bay & Headlight Restoration:</strong> Specialty
            services to keep every part of your vehicle looking its best.
          </span>
        </li>
      </ul>
      </>}

      <h2 data-id="element-683" data-mp="w3x5j0lhhz9e">Neighborhoods We Serve in {city.name}</h2>
      <p data-id="element-684" data-mp="r5vlaccbntwc">
        Our mobile detailing service reaches every corner of {city.name},
        including {city.neighborhoods.slice(0, -1).join(', ')}, and{' '}
        {city.neighborhoods[city.neighborhoods.length - 1]}. Wherever you park,
        we come to you with our own water and detailing equipment. We require
        access to a standard electrical outlet at the service location.
      </p>
      <div className="not-prose flex flex-wrap gap-3 my-6" data-id="element-685" data-mp="d7xhu1zjhhvm">
        {city.neighborhoods.map(n => <span key={n} className="inline-flex items-center gap-2 bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-gray-300" data-id="element-686" data-mp="892pa8brseyb">
            <MapPin className="w-4 h-4 text-red-500" data-id="element-687" data-mp="22m2duqy4ojhq" />
            {n}
          </span>)}
      </div>

      <h2 data-id="element-688" data-mp="2a3if10gdhpwo">Explore Our Detailing Services</h2>
      <p data-id="element-689" data-mp="2a2foitmyppha">
        Learn more about each of our professional mobile detailing services
        available in {city.name}, or head back to our{' '}
        <Link to="/" data-id="element-690" data-mp="2ju39cbp0k89s">mobile detailing homepage</Link> for full pricing and
        packages:
      </p>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-691" data-mp="2otppou0524mt">
        {relatedServices.map(s => <Link key={s.to} to={s.to} className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-692" data-mp="1r6plr477gp2c">
            {s.label} &rarr;
          </Link>)}
      </div>

      <h2 data-id="element-693" data-mp="21681t9jzvqp4">Book Mobile Detailing in {city.name}</h2>
      <p data-id="element-694" data-mp="256i5ugo8871c">
        Call or text Mike's Pro Detail at{' '}
        <a href="tel:8183819905" data-id="element-695" data-mp="3cj0s0fx50732">(818) 381-9905</a>, or{' '}
        <Link to="/contact" data-id="element-696" data-mp="3cih7qsk0l6lv">request a free quote online</Link>. Compare
        packages and pricing on our{' '}
        <Link to="/services" data-id="element-697" data-mp="3gmp1531f3aqf">detailing services page</Link>, or{' '}
        <Link to="/locations" data-id="element-698" data-mp="1te7vq81smi4z">view all service areas</Link>.
      </p>
    </ServicePageLayout>;
}